import { useSession } from 'next-auth/react';

/**
 * Hook to check if the current user has the required permissions
 * @usage
 * This hook is the primary tool for checking user permissions on the client-side.
 * It is used to conditionally render UI elements, such as buttons or menu items, based on the current user's permissions.
 *
 * Key usage locations:
 * - `src/components/common/PermissionCheck.tsx`: A wrapper component that uses this hook to show/hide its children.
 * - `src/components/common/NavbarPermissionGuard.tsx`: Used to control visibility of navigation links.
 * - Component-level checks: e.g., `src/components/grades/GradeActionsMenu.tsx` to show/hide edit/delete options.
 * - Page-level checks: e.g., `src/app/grades/page.tsx` to control access to creating new grades.
 */
export function usePermission() {
  const { data: session, status } = useSession();
  const userPermissions = session?.user?.permissions || [];
  const userRole = session?.user?.role || '';

  const isLoading = status === 'loading';

  /**
   * Check if the user has all of the specified permissions
   * @param permissions - Array of permission strings to check
   * @returns boolean - True if user has all permissions, false otherwise
   */
  const hasAllPermissions = (permissions: string[]): boolean => {
    if (!session?.user) return false;
    if (permissions.length === 0) return true;

    return permissions.every((permission) =>
      userPermissions.includes(permission),
    );
  };

  /**
   * Check if the user has any of the specified permissions
   * @param permissions - Array of permission strings to check
   * @returns boolean - True if user has at least one permission, false otherwise
   */
  const hasAnyPermission = (permissions: string[]): boolean => {
    if (!session?.user) return false;
    if (permissions.length === 0) return true;

    return permissions.some((permission) =>
      userPermissions.includes(permission),
    );
  };

  /**
   * Check if the user has a specific permission
   * @param permission - Permission string to check
   * @returns boolean - True if user has the permission, false otherwise
   */
  const hasPermission = (permission: string): boolean => {
    if (!session?.user) return false;
    return userPermissions.includes(permission);
  };

  return {
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    userPermissions,
    userRole,
    isLoading, // Export the loading status
  };
}
